import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useLocation } from "@reach/router";
import { useCurrentPath } from "../contexts/useCurrentPath";
import { useSiteMetadataVersions } from "../queries/useSiteMetadataVersions";
import { getNormalizedPermalinkFromUrl } from "../utils/getNormalizedPermalinkFromUrl";
import { getNameFromGuide } from "../utils/getNameFromGuide";
import {
  getNormalizedVersionFromReleaseNoteUrl,
  getNormalizedVersionFromUrl,
} from "../utils/getNormalizedVersionFromUrl";
import { getDocCategory } from "../utils/getDocGuide";
import favicon from "../images/favicon.png";
import { useVersionLookup } from "../utils/getVersionLookup";

export const Head: React.FC<{ title: any }> = ({ title }) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          siteUrl
          titleSuffix
          social {
            twitter
          }
          versions
          latestVersion
        }
      }
    }
  `);

  const { pathname } = useLocation();
  const guide = getDocCategory(pathname);
  const { latestVersion } = useSiteMetadataVersions();
  const currentPath = useCurrentPath();
  const permalink = getNormalizedPermalinkFromUrl(pathname);
  const metaDescription = site.siteMetadata.description;
  const version =
    currentPath === "release_notes"
      ? getNormalizedVersionFromReleaseNoteUrl(pathname, latestVersion)
      : getNormalizedVersionFromUrl(pathname, latestVersion);

  let canonicalVersion = version;
  let noIndex = false;
  const versionLookup = useVersionLookup();

  // Does this page have a permalink?
  if (permalink !== null) {
    const versions = versionLookup[permalink]?.versions || [];

    if (versions.length > 0) {
      // Sort versions in descending order, treating `cloud` as a separate entity
      const numberedVersions = versions.filter(v => v !== "cloud").sort((a, b) => {
        const A = a.split(".").map(Number);
        const B = b.split(".").map(Number);
        if (A[0] !== B[0]) {
          return (B[0] || 0) - (A[0] || 0);
        }
        return (B[1] || 0) - (A[1] || 0);
      });

      const latestNumberedVersion = numberedVersions[0];

      // Handle `cloud` and `latest` separately
      if (version === "cloud") {
        canonicalVersion = "cloud"; // Always use `cloud` for cloud docs
      } else if (version === "latest") {
        canonicalVersion = latestNumberedVersion; // `latest` points to the latest numbered version
      } else {
        // For versioned pages, check if this is the latest numbered version
        if (version !== latestNumberedVersion) {
          noIndex = true; // Mark non-latest versions as noindex
        }
        canonicalVersion = latestNumberedVersion; // Canonical points to the latest numbered version
      }
    }
  }

  // If this is not the latest version of the page then add a canonical <link> tag.
  // The URL in the link needs to be absolute.
  //
  // This link should be present on the page even if it's the canonical page.
  //
  const canonical =
    site.siteMetadata.siteUrl +
    pathname.replace(`/${version}/`, `/${canonicalVersion}/`);

  return (
    <>
      <title>
        {title} | {site.siteMetadata.titleSuffix}
      </title>
      <link rel="icon" href={favicon} />
      <meta name="description" content={metaDescription} />
      <meta name="title" content={title} />
      <meta name="version" content={version} />
      <meta name="guide" content={getNameFromGuide(guide)} />
      <meta name="og:title" content={title} />
      <meta name="og:description" content={metaDescription} />
      <meta name="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:creator" content={site.siteMetadata.social.twitter} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {!(canonical == "") && <link rel="canonical" href={canonical}></link>}
      {noIndex && <meta name="robots" content="noindex"></meta>}
    </>
  );
};
